/**
 * Helpers to produce boxShadow and textShadow style values.
 * Replaces deprecated shadow* / textShadow* props with the unified string format.
 */

/** Convert hex color to rgba string with given opacity (0–1). Handles #rgb and #rrggbb. */
function colorToRgba(hexOrRgba: string, opacity: number): string {
  const s = String(hexOrRgba).trim();
  if (s.startsWith('rgba(')) return s;
  if (s.startsWith('rgb(')) {
    const match = s.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) return `rgba(${match[1]},${match[2]},${match[3]},${opacity})`;
  }
  const hex = s.replace(/^#/, '');
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r},${g},${b},${opacity})`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  }
  return `rgba(0,0,0,${opacity})`;
}

export interface BoxShadowParams {
  shadowColor: string;
  shadowOffset: { width: number; height: number };
  shadowOpacity: number;
  shadowRadius: number;
}

/**
 * Returns a style object with boxShadow (CSS string) from the legacy shadow* props.
 * Use this instead of shadowColor, shadowOffset, shadowOpacity, shadowRadius.
 */
export function makeBoxShadow(params: BoxShadowParams): { boxShadow: string } {
  const { shadowColor, shadowOffset, shadowOpacity, shadowRadius } = params;
  const color = colorToRgba(shadowColor, shadowOpacity);
  const x = shadowOffset.width;
  const y = shadowOffset.height;
  return {
    boxShadow: `${x}px ${y}px ${shadowRadius}px 0px ${color}`,
  };
}

export interface TextShadowParams {
  textShadowColor: string;
  textShadowOffset: { width: number; height: number };
  textShadowRadius: number;
}

/**
 * Returns a style object with textShadow (CSS string) from the legacy textShadow* props.
 * Use this instead of textShadowColor, textShadowOffset, textShadowRadius.
 * Assumes full opacity for the color (pass rgba in textShadowColor if you need alpha).
 */
export function makeTextShadow(params: TextShadowParams): { textShadow: string } {
  const { textShadowColor, textShadowOffset, textShadowRadius } = params;
  const color = textShadowColor.startsWith('rgba') ? textShadowColor : colorToRgba(textShadowColor, 1);
  const x = textShadowOffset.width;
  const y = textShadowOffset.height;
  return {
    textShadow: `${x}px ${y}px ${textShadowRadius}px ${color}`,
  };
}
